import React, { useState, useEffect, useCallback, useMemo } from "react";
import { adminApiRequest } from "../utils/adminApi";
import AnalyticsSection from "./AnalyticsSection";
import AnalyticsModals from "./AnalyticsModals";

const formatCustomerName = (cust) =>
  `${cust.first_name || ""} ${cust.last_name || ""}`.trim() ||
  "Unknown Customer";

const getDefaultDates = () => {
  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const defaultStart = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  return { defaultStart, defaultEnd };
};

const initialDetailsModal = {
  open: false,
  title: "",
  type: "",
  loading: false,
  rows: [],
  message: "",
};

const Analytics = () => {
  const { defaultStart, defaultEnd } = getDefaultDates();

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);

  const [modalData, setModalData] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [detailsModal, setDetailsModal] = useState(initialDetailsModal);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await adminApiRequest({
        script: "analytics",
        data: {
          start: periodStart,
          end: periodEnd,
        },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load analytics");
      }

      setAnalytics(data.analytics);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const openDetailsModal = ({ title, type }) => {
    setDetailsModal({
      open: true,
      title,
      type,
      loading: true,
      rows: [],
      message: "",
    });
  };

  const handleViewOrdersByDay = useCallback(async (date) => {
    openDetailsModal({
      title: `Completed Orders - ${date}`,
      type: "ordersByDay",
    });

    try {
      const res = await adminApiRequest({
        script: "analytics-order-by-day",
        data: { date },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load orders");
      }

      setDetailsModal({
        open: true,
        title: `Completed Orders - ${date}`,
        type: "ordersByDay",
        loading: false,
        rows: res.orders || [],
        message: "",
      });
    } catch (err) {
      console.error(err);
      setDetailsModal({
        open: true,
        title: `Completed Orders - ${date}`,
        type: "ordersByDay",
        loading: false,
        rows: [],
        message: err.message || "Failed to load orders",
      });
    }
  }, []);

  const handleViewItemBreakdown = useCallback(
    async (item) => {
      openDetailsModal({
        title: `${item.item_name} - Daily Breakdown`,
        type: "itemBreakdown",
      });

      try {
        const res = await adminApiRequest({
          script: "analytics-item-daily-breakdown",
          data: {
            item_id: item.item_id,
            start: periodStart,
            end: periodEnd,
          },
        });

        if (!res?.success) {
          throw new Error(res?.message || "Failed to load item breakdown");
        }

        setDetailsModal({
          open: true,
          title: `${item.item_name} - Daily Breakdown`,
          type: "itemBreakdown",
          loading: false,
          rows: res.breakdown || [],
          message: "",
        });
      } catch (err) {
        console.error(err);
        setDetailsModal({
          open: true,
          title: `${item.item_name} - Daily Breakdown`,
          type: "itemBreakdown",
          loading: false,
          rows: [],
          message: err.message || "Failed to load item breakdown",
        });
      }
    },
    [periodStart, periodEnd],
  );

  const closeMainModal = () => {
    setModalData(null);
    setModalTitle("");
  };

  const closeDetailsModal = () => {
    setDetailsModal(initialDetailsModal);
  };

  const derived = useMemo(() => {
    if (!analytics) return null;

    const {
      ordersSummary = [],
      topItems = [],
      topCustomers = [],
      ordersByType = [],
      paymentMethods = [],
      totalOrders = 0,
      totalRevenue = 0,
      totalExpenses = 0,
      profitOrLoss = 0,
      newCustomers = 0,
      returningCustomers = 0,
    } = analytics;

    const sortedOrdersSummary = [...ordersSummary].sort(
      (a, b) => new Date(b.period) - new Date(a.period),
    );

    const bestSeller = topItems[0] || null;

    const actualCustomers = topCustomers.filter((cust) => !cust.is_internal);

    const topCustomerValue = actualCustomers[0] || null;

    const topCustomerVolume = actualCustomers.reduce(
      (max, c) => (c.total_orders > (max?.total_orders || 0) ? c : max),
      null,
    );

    const topCustomersByVolume = [...topCustomers].sort(
      (a, b) => b.total_orders - a.total_orders,
    );

    const totalOrderTypeRevenue = ordersByType.reduce(
      (sum, item) => sum + Number(item.revenue || 0),
      0,
    );

    const totalPaymentMethodRevenue = paymentMethods.reduce(
      (sum, item) => sum + Number(item.total_revenue || 0),
      0,
    );

    const statCards = [
      {
        title: "Total Orders",
        value: totalOrders,
        icon: "🧾",
        accent: "bg-sky-500",
      },
      {
        title: "Gross Income",
        value: `£${Number(totalRevenue).toFixed(2)}`,
        icon: "💰",
        accent: "bg-emerald-500",
      },
      {
        title: "Expenses",
        value: `£${Number(totalExpenses).toFixed(2)}`,
        icon: "🧾",
        accent: "bg-amber-500",
      },
      {
        title: "Profit / Loss",
        value: `£${Number(profitOrLoss).toFixed(2)}`,
        icon: profitOrLoss < 0 ? "📉" : "📈",
        accent: profitOrLoss < 0 ? "bg-red-500" : "bg-emerald-500",
        valueClass: profitOrLoss < 0 ? "text-red-500" : "text-emerald-600",
      },
      {
        title: "Best Seller",
        value:
          totalOrders > 0 && bestSeller?.quantity_sold > 0
            ? bestSeller.item_name
            : "N/A",
        icon: "🍽️",
        accent: "bg-violet-500",
      },
      {
        title: "Top Customer Value",
        value:
          totalOrders > 0 && topCustomerValue?.total_value > 0
            ? formatCustomerName(topCustomerValue)
            : "N/A",
        icon: "👑",
        accent: "bg-indigo-500",
      },
      {
        title: "Top Customer Volume",
        value:
          totalOrders > 0 && topCustomerVolume?.total_orders > 0
            ? formatCustomerName(topCustomerVolume)
            : "N/A",
        icon: "🏆",
        accent: "bg-cyan-500",
      },
      {
        title: "New Customers",
        value: newCustomers,
        icon: "🆕",
        accent: "bg-pink-500",
      },
      {
        title: "Returning Customers",
        value: returningCustomers,
        icon: "🔁",
        accent: "bg-orange-500",
      },
    ];

    const sectionConfigs = [
      {
        title: "Orders Summary",
        modalTitle: "Orders Summary",
        headers: ["Date", "Total Orders", "Total Revenue (£)"],
        data: sortedOrdersSummary,
        previewRows: sortedOrdersSummary.slice(0, 5).map((row) => ({
          onClick: () => handleViewOrdersByDay(row.period),
          cells: [
            row.period,
            row.total_orders,
            Number(row.total_revenue || 0).toFixed(2),
          ],
        })),
      },
      {
        title: "Top Selling Items",
        modalTitle: "All Top Selling Items",
        headers: ["Item Name", "Quantity Sold", "Total Revenue (£)"],
        data: topItems,
        previewRows: topItems.slice(0, 5).map((item) => ({
          onClick: () => handleViewItemBreakdown(item),
          cells: [
            item.item_name,
            item.quantity_sold,
            Number(item.total_revenue || 0).toFixed(2),
          ],
        })),
      },
      {
        title: "Top Customer (Value)",
        modalTitle: "Top Customers (Value)",
        headers: ["Name", "Phone", "Total Value (£)"],
        data: topCustomers,
        previewRows: topCustomers.slice(0, 5).map((cust) => ({
          isInternal: cust.is_internal,
          cells: [
            {
              type: "customer",
              label: formatCustomerName(cust),
              isInternal: cust.is_internal,
            },
            cust.phone || "—",
            Number(cust.total_value || 0).toFixed(2),
          ],
        })),
      },
      {
        title: "Top Customer (Volume)",
        modalTitle: "Top Customers (Volume)",
        headers: ["Name", "Phone", "Total Orders"],
        data: topCustomersByVolume,
        previewRows: topCustomersByVolume.slice(0, 5).map((cust) => ({
          isInternal: cust.is_internal,
          cells: [
            {
              type: "customer",
              label: formatCustomerName(cust),
              isInternal: cust.is_internal,
            },
            cust.phone || "—",
            cust.total_orders,
          ],
        })),
      },
    ];

    return {
      ordersByType,
      totalOrderTypeRevenue,
      paymentMethods,
      totalPaymentMethodRevenue,
      statCards,
      sectionConfigs,
    };
  }, [analytics, handleViewOrdersByDay, handleViewItemBreakdown]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="p-6 text-sm font-medium text-red-500">Error: {error}</p>
    );
  }

  if (!analytics || !derived) {
    return <p className="p-6 text-sm text-slate-500">No data available</p>;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-2">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Analytics Dashboard
          </h1>

          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Start
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-4 focus:ring-slate-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                End
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-4 focus:ring-slate-100"
              />
            </div>

            <button
              className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              onClick={fetchAnalytics}
            >
              Apply
            </button>
          </div>
        </div>

        <AnalyticsSection type="stats" statCards={derived.statCards} />

        <AnalyticsSection
          type="tables"
          sectionConfigs={derived.sectionConfigs}
          onViewAll={(data, title) => {
            setModalData(data);
            setModalTitle(title);
          }}
        />

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[2fr_1fr]">
          <AnalyticsSection
            type="ordersByType"
            ordersByType={derived.ordersByType}
            totalOrderTypeRevenue={derived.totalOrderTypeRevenue}
          />

          <AnalyticsSection
            type="paymentMethods"
            paymentMethods={derived.paymentMethods}
            totalPaymentMethodRevenue={derived.totalPaymentMethodRevenue}
          />
        </div>

        <AnalyticsModals
          modalData={modalData}
          modalTitle={modalTitle}
          detailsModal={detailsModal}
          onCloseMain={closeMainModal}
          onCloseDetails={closeDetailsModal}
        />
      </div>
    </div>
  );
};

export default Analytics;
